import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronRight, FiChevronDown, FiCamera, FiMonitor, FiZap, FiBattery, FiStar, FiArrowLeft, FiArrowRight, FiExternalLink, FiPrinter } from 'react-icons/fi';
import VideoBubble from './VideoBubble';
import { getApiUrl } from '../../utils/apiUrl';

// Get API URL for this environment
const API_URL = getApiUrl();

// Default images URLs from the original WordPress site (fallback)
const DEFAULT_IMAGES = {
  logo: 'https://2025.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-2048x2048.png',
  photobooth: 'https://2025.justasmile-events.com/wp-content/uploads/2022/07/photobooth-JUSTASMILE-1.png',
  photoboothMain: 'https://2025.justasmile-events.com/wp-content/uploads/2025/09/image00001-1-683x1024.jpeg',
  gallery: [
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/F0F6E4CE-2874-4085-8826-A11A8418C01D.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/E7416180-3CC5-44BA-A120-12EB19536993.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/IMG_9649.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/B1290EE8-ACD7-4108-9AC0-015574EFD420-1.jpg',
  ],
  models: [
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/30-ans-Tiffany.png', alt: '30 ans Tiffany' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/bapteme-2.png', alt: 'bapteme 2' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/bapteme.png', alt: 'bapteme' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/dragonball.png', alt: 'dragonball' },
    { src: 'https://2025.justasmile-events.com/wp-content/uploads/2022/12/mariage.png', alt: 'mariage' },
  ],
};

// Default Hotspot data matching the original WordPress page (https://2025.justasmile-events.com/location-de-photobooth/)
const DEFAULT_HOTSPOTS = [
  {
    id: 'flash',
    label: 'Flash Professionnel',
    icon: 'zap',
    tooltip: 'Permet une luminosité et une qualité optimales',
    positionLeft: '15%',
    positionTop: '8%',
  },
  {
    id: 'camera',
    label: 'Appareil photo',
    icon: 'camera',
    tooltip: 'avec un objectif 18-55',
    positionLeft: '48%',
    positionTop: '12%',
  },
  {
    id: 'led',
    label: 'Lumières LED',
    icon: 'zap',
    tooltip: 'Pour vous mettre en lumière',
    positionLeft: '25%',
    positionTop: '28%',
  },
  {
    id: 'screen',
    label: 'Ecran tactile',
    icon: 'monitor',
    tooltip: 'Pour choisir vos modèles et voir l\'aperçu de vos photos',
    positionLeft: '48%',
    positionTop: '35%',
  },
  {
    id: 'printer',
    label: 'Imprimante',
    icon: 'printer',
    tooltip: 'Impression instantanée de vos photos en haute qualité',
    positionLeft: '48%',
    positionTop: '52%',
  },
  {
    id: 'ups',
    label: 'Onduleur intégré',
    icon: 'battery',
    tooltip: 'Un onduleur permettant au PhotoBooth de ne pas s\'éteindre lors d\'un débranchement involontaire ou d\'une coupure de courant',
    positionLeft: '48%',
    positionTop: '88%',
  },
];

// Default FAQ Data matching the original WordPress page
const DEFAULT_FAQ = [
  {
    question: 'Pourquoi louer un PhotoBooth chez Just a Smile Events ?',
    answer: `<p>Pour rendre votre événement <strong>UNIQUE</strong> !</p>
<p>Nos graphistes créent pour chaque événement un ou plusieurs modèles en fonction de votre thème et de vos envies.</p>
<p>Nous mettons aussi tout en œuvre pour rendre l'activité <strong>MAGIQUE</strong> et en mettre plein la vue à vos invités en installant notre PhotoBooth avec un tapis rouge, des poteaux or de guidage, des cordes de couleur en fonction de votre décoration, des accessoires ludiques…</p>
<p>Notre borne à selfie peut aussi bien être utilisée de jour comme de nuit, en intérieur comme en extérieur. Totalement autonome, elle enregistre numériquement toutes les photos prises dans une galerie sécurisée.</p>
<p>Cette dernière vous sera partagée après l'événement.</p>
<p>Nous proposons un service professionnel et de qualité, joignable 24h/24 et 7j/7.</p>`,
  },
  {
    question: 'Quel matériel utilisons-nous ?',
    answer: `<p>Notre PhotoBooth est composé</p>
<ul>
<li>d'un appareil photo Canon professionnel</li>
<li>d'une imprimante thermique DNP de haute qualité</li>
<li>d'un grand écran tactile</li>
<li>de lumières LED permettant d'apporter une luminosité idéale à vos photos.</li>
<li>d'un flash professionnel permettant une qualité digne d'un studio photo</li>
</ul>`,
  },
  {
    question: 'Comment utiliser le PhotoBooth ?',
    answer: `<p>Le principe est simple : placez vous devant la borne à selfie, sélectionnez le modèle créé spécialement pour vous et prenez la pose.</p>
<p>Un joli sourire et le PhotoBooth s'occupe de photographier le moment présent. Vous lancez l'impression et en quelques secondes la photo est entre vos mains !</p>`,
  },
  {
    question: 'Comment se passe l\'installation ?',
    answer: `<p>Pas de panique ! L'installation est rapide, notre équipe a besoin d'environ 45 minutes pour que le dispositif soit mis en place et fonctionnel.</p>
<p>Pour une installation en extérieur, le PhotoBooth doit obligatoirement être installé à l'ombre ou sous un abri. Si la météo ne le permet pas, il sera automatiquement installé en intérieur</p>
<p>Nous avons besoin d'une surface plane.</p>`,
  },
  {
    question: 'Quels sont les différents formats photo ?',
    answer: `<p>Notre sublime imprimante a la possibilité d'imprimer vos photos sous différents formats :</p>
<ul>
<li>Carte postale (10×15) horizontal ou vertical</li>
<li>Photomaton (5×15) en bandelettes</li>
</ul>`,
  },
];

// Icon mapping - supports both text keys and emoji keys
const ICON_MAP = {
  camera: FiCamera,
  monitor: FiMonitor,
  zap: FiZap,
  battery: FiBattery,
  printer: FiPrinter,
  // Emoji mappings for database data
  '📷': FiCamera,
  '📹': FiCamera,
  '🎥': FiCamera,
  '📱': FiMonitor,
  '💡': FiZap,
  '⚡': FiZap,
  '🔋': FiBattery,
  '🎨': FiStar,
  '🎤': FiMonitor,
};

export default function PhotoboothPresentationPage({ pageData, productData, onNavigate, onBookingClick }) {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [currentModelIndex, setCurrentModelIndex] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [hotspotsFromDB, setHotspotsFromDB] = useState(null);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleReviewsInfo, setGoogleReviewsInfo] = useState({ rating: 5.0, total: 89 });
  const videoRef = useRef(null);

  // Load hotspots from database
  useEffect(() => {
    const loadHotspots = async () => {
      try {
        const response = await fetch(`${API_URL}/api/hotspots-config`);
        if (response.ok) {
          const data = await response.json();
          if (data.photobooth) {
            setHotspotsFromDB(data.photobooth);
          }
        }
      } catch (error) {
        console.log('Using default hotspots');
      }
    };
    loadHotspots();
  }, []);

  // Load Google Reviews (same as homepage)
  useEffect(() => {
    const loadGoogleReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/google-reviews/`);
        if (response.ok) {
          const data = await response.json();
          if (data.reviews && data.reviews.length > 0) {
            const formattedReviews = data.reviews.map(r => ({
              name: r.author_name || r.name,
              rating: r.rating,
              text: r.text,
              date: r.relative_time_description || r.date,
              avatar: r.profile_photo_url || r.avatar
            }));
            setGoogleReviews(formattedReviews);
            setGoogleReviewsInfo({
              rating: data.average_rating || 5.0,
              total: data.total_reviews || formattedReviews.length
            });
          }
        }
      } catch (error) {
        console.log('Using fallback reviews');
      }
    };
    loadGoogleReviews();
  }, []);

  // Helper to find section by type
  const getSection = (type) => {
    return pageData?.sections?.find(s => s.type === type)?.content || null;
  };

  // Get content from pageData sections - check for actual content, not just empty object
  const heroSection = getSection('hero_video') || getSection('hero') || {};
  const textImageSection = getSection('text_image') || {};
  const featuresSection = getSection('features_icons') || {};
  const hotspotsSection = getSection('hotspots') || {};
  const faqSection = getSection('faq') || {};
  const reviewsSection = getSection('reviews') || {};
  const modelsSection = getSection('models') || getSection('models_gallery') || {};
  const gallerySection = getSection('presentation_gallery') || getSection('gallery') || {};

  // Map sections to expected content structure
  const heroContent = {
    title: heroSection.title || 'LOCATION DE PHOTOBOOTH',
    subtitle: heroSection.subtitle || '',
    videoUrl: heroSection.videoUrl || 'https://www.youtube.com/watch?v=WwXvqrKiKBE',
    buttonText: heroSection.buttonText || 'RÉSERVATION',
    backgroundImage: heroSection.backgroundImage || heroSection.image || '',
    overlayOpacity: heroSection.overlayOpacity || 0.6,
  };
  
  const presentationContent = {
    title: textImageSection.title || '',
    text: textImageSection.text || '',
    highlightText: textImageSection.highlightText || '',
    image: textImageSection.image || '',
    paragraphs: textImageSection.paragraphs || (textImageSection.text ? [textImageSection.text] : []),
  };
  
  const hotspotsContent = hotspotsSection || {};
  const modelsContent = modelsSection || {};
  const faqContent = faqSection || {};
  const reviewsContent = reviewsSection || {};
  
  // Installation video URL (separate from hero background video)
  // Priority: text_image section videoUrl > installation_video section > hero installationVideoUrl > default
  const installationVideoSection = getSection('installation_video') || {};
  const installationVideoUrl = textImageSection.videoUrl || installationVideoSection.videoUrl || heroSection.installationVideoUrl || 'https://www.youtube.com/watch?v=WwXvqrKiKBE';

  // Use video URL from hero section (Constructor only - Option B)
  const getVideoId = () => {
    // Extract video ID from hero section URL (Constructor)
    const videoUrl = heroContent.videoUrl || '';
    if (videoUrl) {
      const match = videoUrl.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?\n]+)/);
      if (match) {
        return match[1];
      }
    }
    // Default fallback (vidéo par défaut si rien configuré dans le Constructor)
    return 'WwXvqrKiKBE';
  };
  const heroVideoId = getVideoId();

  // Images
  const IMAGES = {
    logo: presentationContent.logo || DEFAULT_IMAGES.logo,
    photobooth: presentationContent.photoboothImage || DEFAULT_IMAGES.photobooth,
    photoboothMain: hotspotsFromDB?.image || hotspotsContent.mainImage || DEFAULT_IMAGES.photoboothMain,
    gallery: gallerySection?.images || presentationContent.galleryImages || DEFAULT_IMAGES.gallery,
    models: modelsContent.images || modelsContent.carouselImages || DEFAULT_IMAGES.models,
  };

  // Hotspots - Use DB data (x/y format) or fall back to section items or defaults
  const HOTSPOTS = (hotspotsFromDB?.hotspots || hotspotsContent.items || DEFAULT_HOTSPOTS).map(h => ({
    ...h,
    id: h.id,
    label: h.label,
    tooltip: h.tooltip || h.description || '',
    icon: ICON_MAP[h.icon] || FiZap,
    position: { 
      left: h.positionLeft || `${h.x}%`, 
      top: h.positionTop || `${h.y}%` 
    }
  }));

  // FAQ
  const FAQ_DATA = faqContent.items || DEFAULT_FAQ;

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentModelIndex((prev) => (prev + 1) % IMAGES.models.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, IMAGES.models.length]);

  const nextModel = () => {
    setIsAutoPlaying(false);
    setCurrentModelIndex((prev) => (prev + 1) % IMAGES.models.length);
  };

  const prevModel = () => {
    setIsAutoPlaying(false);
    setCurrentModelIndex((prev) => (prev - 1 + IMAGES.models.length) % IMAGES.models.length);
  };

  // Text content with defaults - check for actual content, not just empty arrays
  const presentationTitle = presentationContent.title || "<strong>Just a Smile Events</strong> vous propose <br>la location de <strong>PhotoBooth</strong> pour immortaliser vos événements.";
  
  // PRIORITIZE text field from Constructor over paragraphs
  const presentationText = presentationContent.text || "";
  const presentationParagraphs = presentationText 
    ? [presentationText]
    : (presentationContent.paragraphs && presentationContent.paragraphs.length > 0) 
      ? presentationContent.paragraphs 
      : [
          "Nos PhotoBooth vous assurent des photos imprimées instantanément et de haute qualité.",
          "Le principe est simple, nous prenons connaissance de vos envies et du thème de votre événement puis nous vous créons un modèle personnalisé offert en fonction de vos envies.",
          "Optez pour un service professionnel et de qualité et n'hésitez pas à faire votre demande de réservation. Vous serez recontactés sous 24h par notre équipe afin de vous établir un devis personnalisé."
        ];
  
  const hotspotsTitle = hotspotsContent.title || "Qu'est-ce qu'un <strong>PhotoBooth ?</strong>";
  
  // Use description fields from Constructor, fallback to paragraphs array, then defaults
  const hotspotsParagraphs = (hotspotsContent.description || hotspotsContent.description2)
    ? [hotspotsContent.description, hotspotsContent.description2].filter(Boolean)
    : (hotspotsContent.paragraphs && hotspotsContent.paragraphs.length > 0)
      ? hotspotsContent.paragraphs
      : [
          "Il s'agit d'une borne à selfie qui permet de capturer des souvenirs pour tous types d'événements : anniversaire, mariage, baptême, soirées d'entreprise, EVJF/EVJG, baby shower, gender reveal…",
          "Chaque invité peut repartir avec une ou plusieurs impressions de photo et/ou l'avoir directement sur son téléphone en scannant un QRCode à l'écran."
        ];
  
  const modelsTitle = modelsContent.title || "<strong>Just a Smile Events</strong> vous propose <br>des modèles photo personnalisés pour un résultat à votre image !";
  const modelsText = modelsContent.description || modelsContent.text || "Vous nous envoyez vos instructions, et nos graphistes réalisent vos encadrements de photo. Nous nous démarquons en proposant des modèles personnalisés pour chaque événement. Ils sont crées chaque semaine par notre équipe pour répondre au mieux à vos besoins et pour rendre votre événement unique. Pour plus de souvenirs, vous pouvez avoir plusieurs modèles différents durant la même soirée et vous n'aurez qu'à choisir à l'écran celui que vous voulez avant de lancer votre photo !";
  const modelsButtonText = modelsContent.buttonText || "Découvrir nos modèles";
  const modelsButtonLink = (modelsContent.buttonLink || "photobooth-modeles").replace(/^\//, '');
  
  const faqTitle = faqContent.title || "Vous avez des questions ? Consultez notre FAQ :";
  const reviewsTitle = reviewsContent.title || "Qui de mieux que vous, pour en parler ?";

  return (
    <div className="photobooth-presentation-page bg-black" data-testid="photobooth-presentation-page">
      {/* Section 1: Hero with YouTube Video Background - Reduced Height */}
      <section className="relative h-[85vh] flex items-end justify-center overflow-hidden pb-24">
        {/* YouTube Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <iframe
            ref={videoRef}
            src={`https://www.youtube.com/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&playlist=${heroVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-150"
            style={{ 
              pointerEvents: 'none',
              border: 'none',
              width: '100vw',
              height: '100vh',
              minWidth: '177.78vh',
              minHeight: '100vh'
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="PhotoBooth Video"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="w-full h-16">
            <path fill="#000000" d="M421.9,6.5c22.6-2.5,51.5,0.4,75.5,5.3c23.6,4.9,70.9,23.5,100.5,35.7c75.8,32.2,133.7,44.5,192.6,49.7c23.6,2.1,48.7,3.5,103.4-2.5c54.7-6,106.2-25.6,106.2-25.6V100H0V30.3c0,0,72,32.6,158.4,30.5c39.2-0.7,92.8-6.7,134-22.4c21.2-8.1,52.2-18.2,79.7-24.2C399.3,7.9,411.6,7.5,421.9,6.5z"></path>
          </svg>
        </div>

        {/* Content - Positioned near the wave */}
        <div className="relative z-10 text-center px-6 mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-wide"
          >
            {heroContent.title}
          </motion.h2>
          {heroContent.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto"
            >
              {heroContent.subtitle}
            </motion.p>
          )}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            onClick={(e) => onBookingClick?.(e, 'photobooth')}
            className="bg-[#e6d28e] hover:bg-[#d4c67a] text-black font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            data-testid="hero-reservation-btn"
          >
            {heroContent.buttonText}
          </motion.button>
        </div>
      </section>

      {/* Section 2: Presentation with Logo and Gallery - Dark background */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Logo + Text */}
            <div className="relative">
              <div className="text-center md:text-left">
                <img
                  src={IMAGES.logo}
                  alt="Just A Smile Events Logo"
                  className="w-32 h-32 mx-auto md:mx-0 mb-6"
                />
                <h2 
                  className="text-2xl md:text-3xl font-medium text-white mb-6 leading-relaxed [&_strong]:text-[#e6d28e]"
                  dangerouslySetInnerHTML={{ __html: presentationTitle }}
                />
                <div className="text-white/70 space-y-4 leading-relaxed [&_strong]:font-bold [&_strong]:text-[#e6d28e] [&_em]:italic [&_u]:underline">
                  {presentationParagraphs.map((p, idx) => (
                    <div key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Presentation Video (replaces gallery) */}
            <div className="transform rotate-[3deg]">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative rounded-xl overflow-hidden shadow-2xl border border-[#e6d28e]/20"
              >
                {/* Video Embed */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${(() => {
                      const videoUrl = installationVideoUrl || heroContent.videoUrl || '';
                      const match = videoUrl.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?\n]+)/);
                      return match ? match[1] : 'WwXvqrKiKBE';
                    })()}?rel=0&modestbranding=1`}
                    title="Présentation"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Interactive Hotspots Image - Dark section */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Hotspot Image */}
            <div className="relative w-full max-w-lg mx-auto">
              <img
                src={IMAGES.photoboothMain}
                alt="PhotoBooth avec fonctionnalités"
                className="w-full rounded-lg shadow-xl"
              />
              
              {/* Hotspots - Visible on all devices - Shows short title, expands detail on click */}
              <div className="absolute inset-0">
                {HOTSPOTS.map((hotspot) => {
                  const IconComponent = hotspot.icon;
                  const isActive = activeHotspot === hotspot.id;
                  return (
                    <div
                      key={hotspot.id}
                      className="absolute"
                      style={{ left: hotspot.position.left, top: hotspot.position.top }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        animate={{ 
                          scale: isActive ? 1 : [1, 1.03, 1],
                          boxShadow: isActive 
                            ? '0 0 20px rgba(230, 210, 142, 0.8)' 
                            : ['0 0 0px rgba(230, 210, 142, 0)', '0 0 10px rgba(230, 210, 142, 0.4)', '0 0 0px rgba(230, 210, 142, 0)']
                        }}
                        transition={{ 
                          duration: 2, 
                          repeat: isActive ? 0 : Infinity,
                          ease: "easeInOut"
                        }}
                        onClick={() => setActiveHotspot(isActive ? null : hotspot.id)}
                        className={`flex items-center gap-1.5 rounded-full shadow-lg transition-all duration-300 px-2 py-1.5 sm:px-3 sm:py-2 ${
                          isActive
                            ? 'bg-[#e6d28e] text-black z-20'
                            : 'bg-white/95 text-gray-800 hover:bg-[#e6d28e] hover:text-black'
                        }`}
                      >
                        <IconComponent className="flex-shrink-0 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        {/* Always show short title */}
                        <span className="text-[10px] sm:text-xs font-medium whitespace-nowrap max-w-[60px] sm:max-w-[80px] truncate">
                          {hotspot.label}
                        </span>
                      </motion.button>
                      
                      {/* Expanded detail tooltip - shows on click */}
                      <AnimatePresence>
                        {isActive && hotspot.tooltip && (
                          <motion.div
                            initial={{ opacity: 0, y: 5, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 5, scale: 0.95 }}
                            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-black/90 backdrop-blur-sm text-white text-xs sm:text-sm p-3 sm:p-4 rounded-xl shadow-2xl w-44 sm:w-56 text-center z-30 border border-[#e6d28e]/30"
                          >
                            <div className="font-semibold text-[#e6d28e] mb-1">{hotspot.label}</div>
                            <div className="text-white/80 leading-relaxed">{hotspot.tooltip}</div>
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-3 h-3 bg-black/90 transform rotate-45 mb-[-6px] border-l border-t border-[#e6d28e]/30" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
              
              {/* Mobile: Feature list below image - more prominent */}
            </div>

            {/* Right: What is PhotoBooth */}
            <div>
              <h2 
                className="text-3xl md:text-4xl font-medium mb-6"
              >
                <span className="text-white">Découvrez notre </span>
                <span className="gold-shimmer-text">PhotoBooth</span>
              </h2>
              <div className="text-white space-y-4 leading-relaxed text-lg [&_strong]:font-bold [&_strong]:text-[#e6d28e] [&_em]:italic [&_u]:underline">
                {hotspotsParagraphs.map((p, idx) => (
                  <div key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Personalized Models with Carousel - Dark section */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <h2 
                className="text-2xl md:text-3xl font-medium text-white mb-6 leading-relaxed [&_strong]:text-[#e6d28e]"
                dangerouslySetInnerHTML={{ __html: modelsTitle }}
              />
              <div 
                className="text-white/70 space-y-4 leading-relaxed [&_strong]:font-bold [&_em]:italic [&_u]:underline [&_.gold-shimmer-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: modelsText }}
              />
              <button
                onClick={() => {
                  console.log('Models button clicked, link:', modelsButtonLink, 'onNavigate:', typeof onNavigate);
                  if (onNavigate) {
                    onNavigate(modelsButtonLink);
                  } else {
                    window.location.href = '/' + modelsButtonLink;
                  }
                }}
                className="mt-6 bg-[#e6d28e] hover:bg-[#d4c67a] text-black font-medium px-6 py-3 rounded transition-all duration-300"
                data-testid="discover-models-btn"
              >
                {modelsButtonText}
              </button>
            </div>

            {/* Right: Carousel - Fixed transition */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-black" style={{ minHeight: '400px' }}>
                {IMAGES.models.map((model, index) => {
                  const imgUrl = model?.url || model?.src || model;
                  const fullUrl = imgUrl && imgUrl.startsWith('/api') 
                    ? `${API_URL}${imgUrl}` 
                    : imgUrl;
                  
                  return (
                    <motion.img
                      key={index}
                      src={fullUrl}
                      alt={model?.alt || `Model ${index + 1}`}
                      initial={false}
                      animate={{ 
                        opacity: currentModelIndex === index ? 1 : 0,
                        zIndex: currentModelIndex === index ? 1 : 0
                      }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      className="absolute top-0 left-0 w-full h-full object-contain"
                      style={{ 
                        imageRendering: 'high-quality',
                      }}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  );
                })}
              </div>
              
              {/* Navigation arrows - z-index higher than images */}
              <button
                onClick={prevModel}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all z-10"
                data-testid="carousel-prev-btn"
              >
                <FiArrowLeft className="w-6 h-6 text-black" />
              </button>
              <button
                onClick={nextModel}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all z-10"
                data-testid="carousel-next-btn"
              >
                <FiArrowRight className="w-6 h-6 text-black" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: FAQ Accordion - Dark section */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-medium text-center mb-12">
            <span className="gold-shimmer-text">Questions fréquentes</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left column - first 2 questions */}
            <div className="space-y-4">
              {FAQ_DATA.slice(0, 2).map((faq, idx) => (
                <div key={idx} className="bg-gray-900 rounded-lg shadow-md overflow-hidden border border-gray-800">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                    className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-800 transition-colors"
                    data-testid={`faq-question-${idx}`}
                  >
                    {openFaqIndex === idx ? (
                      <FiChevronDown className="w-5 h-5 text-[#e6d28e] flex-shrink-0" />
                    ) : (
                      <FiChevronRight className="w-5 h-5 text-[#e6d28e] flex-shrink-0" />
                    )}
                    <span className="font-semibold text-white">{faq.question}</span>
                  </button>
                  <AnimatePresence>
                    {openFaqIndex === idx && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div
                          className="p-5 pt-0 text-gray-300 prose prose-base prose-invert max-w-none leading-loose [&_strong]:text-white [&_strong]:font-bold [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-4 [&_li]:my-1"
                          dangerouslySetInnerHTML={{ __html: faq.answer }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Right column - remaining questions */}
            <div className="space-y-4">
              {FAQ_DATA.slice(2).map((faq, idx) => {
                const actualIdx = idx + 2;
                return (
                  <div key={actualIdx} className="bg-gray-900 rounded-lg shadow-md overflow-hidden border border-gray-800">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === actualIdx ? null : actualIdx)}
                      className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-800 transition-colors"
                      data-testid={`faq-question-${actualIdx}`}
                    >
                      {openFaqIndex === actualIdx ? (
                        <FiChevronDown className="w-5 h-5 text-[#e6d28e] flex-shrink-0" />
                      ) : (
                        <FiChevronRight className="w-5 h-5 text-[#e6d28e] flex-shrink-0" />
                      )}
                      <span className="font-semibold text-white">{faq.question}</span>
                    </button>
                    <AnimatePresence>
                      {openFaqIndex === actualIdx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div
                            className="p-5 pt-0 text-gray-300 prose prose-base prose-invert max-w-none leading-loose [&_strong]:text-white [&_strong]:font-bold [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-4 [&_li]:my-1"
                            dangerouslySetInnerHTML={{ 
                              __html: (faq.answer || '')
                                .replace(/&lt;/g, '<')
                                .replace(/&gt;/g, '>')
                                .replace(/&amp;/g, '&')
                                .replace(/&quot;/g, '"')
                            }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews section removed - using scrolling marquee in Footer instead */}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {showLightbox && lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-5xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowLightbox(false)}
                className="absolute -top-12 right-0 text-white/80 hover:text-white text-4xl font-light z-10"
              >
                ×
              </button>
              <img
                src={lightboxImage}
                alt="Gallery"
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
